import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import {
  listProductDeliverables,
  createProductDeliverable,
  updateProductDeliverable,
  deleteProductDeliverable,
  reorderProductDeliverables,
  getProduct,
  type ProductDeliverable,
  type Product,
  type DeliverableType,
} from "../../../api/catalog";
import { Button } from "../../primitives/Button";
import { Modal } from "../../primitives/Modal";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { IconButton } from "../../primitives/IconButton";
import { ProductAlerts } from "./ProductAlerts";
import { useProductAlerts } from "./useProductAlerts";

type ProductDeliverablesTabProps = {
  productId: string;
};

const EVIDENCE_TYPE_OPTIONS = [
  { value: "none", label: "Aucun" },
  { value: "file", label: "Fichier" },
  { value: "link", label: "Lien" },
  { value: "note", label: "Note" },
  { value: "system_flag", label: "Drapeau système" },
];

const DELIVERABLE_TYPE_OPTIONS: { value: DeliverableType; label: string }[] = [
  { value: "customer", label: "Livré au client" },
  { value: "supplier", label: "Attendu du fournisseur" },
];

/** Génère un code à partir du nom (ex: "Activation compte" → "activation_compte"). */
function slugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "") || "livrable";
}

export function ProductDeliverablesTab({ productId }: ProductDeliverablesTabProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [deliverables, setDeliverables] = useState<ProductDeliverable[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState<ProductDeliverable | null>(null);

  // Load product for alerts
  useEffect(() => {
    getProduct(productId).then(setProduct).catch(() => null);
  }, [productId]);

  const alerts = useProductAlerts(product, null, null, [], deliverables, []);
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    is_mandatory: true,
    sort_order: 0,
    category: "",
    evidence_type: "none",
    is_customer_visible: true,
    acceptance_required: false,
    deliverable_type: "customer" as DeliverableType,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadDeliverables();
  }, [productId]);

  const loadDeliverables = async () => {
    setIsLoading(true);
    try {
      const data = await listProductDeliverables(productId);
      setDeliverables(data.sort((a, b) => a.sort_order - b.sort_order));
    } catch (error) {
      console.error("Failed to load deliverables:", error);
      alert(`Erreur lors du chargement: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDeliverable(null);
    setForm({
      code: "",
      name: "",
      description: "",
      is_mandatory: true,
      sort_order: deliverables.length + 1,
      category: "",
      evidence_type: "none",
      is_customer_visible: true,
      acceptance_required: false,
      deliverable_type: "customer",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (deliverable: ProductDeliverable) => {
    setEditingDeliverable(deliverable);
    setForm({
      code: deliverable.code,
      name: deliverable.name,
      description: deliverable.description || "",
      is_mandatory: deliverable.is_mandatory,
      sort_order: deliverable.sort_order,
      category: deliverable.category || "",
      evidence_type: deliverable.evidence_type,
      is_customer_visible: deliverable.is_customer_visible,
      acceptance_required: deliverable.acceptance_required,
      deliverable_type: deliverable.deliverable_type ?? "customer",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const code = editingDeliverable ? form.code : slugFromName(form.name);
    const payload = {
      ...form,
      code,
      category: null,
    };

    try {
      if (editingDeliverable) {
        await updateProductDeliverable(productId, editingDeliverable.id, payload);
      } else {
        await createProductDeliverable(productId, payload);
      }
      await loadDeliverables();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save deliverable:", error);
      alert(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
    }
  };

  const handleDelete = async (deliverableId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce livrable ?")) return;
    try {
      await deleteProductDeliverable(productId, deliverableId);
      await loadDeliverables();
    } catch (error) {
      console.error("Failed to delete deliverable:", error);
      alert(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
    }
  };

  const handleReorder = async (newOrder: ProductDeliverable[]) => {
    try {
      await reorderProductDeliverables(
        productId,
        newOrder.map((d) => d.id)
      );
      setDeliverables(newOrder);
    } catch (error) {
      console.error("Failed to reorder deliverables:", error);
      alert(`Erreur lors du réordonnancement: ${(error as Error).message}`);
      await loadDeliverables(); // Reload on error
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  const customerDeliverables = deliverables.filter((d) => (d.deliverable_type ?? "customer") === "customer");
  const supplierDeliverables = deliverables.filter((d) => (d.deliverable_type ?? "customer") === "supplier");

  const renderDeliverableRow = (deliverable: ProductDeliverable) => (
    <div
      key={deliverable.id}
      className="flex items-center gap-3 rounded-md border border-border bg-surface p-3"
    >
      <div className="flex items-center gap-2 flex-1">
        <div className="text-muted cursor-move">
          <GripVertical size={16} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-text">{deliverable.name}</span>
            {(deliverable.deliverable_type ?? "customer") === "supplier" && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                Fournisseur
              </span>
            )}
            {deliverable.is_mandatory && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-warning/10 text-warning">
                Obligatoire
              </span>
            )}
            {!deliverable.is_customer_visible && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                Interne
              </span>
            )}
          </div>
          {deliverable.description && (
            <p className="text-xs text-muted mt-1">{deliverable.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <IconButton
          icon={<Pencil size={14} />}
          ariaLabel="Modifier"
          intent="ghost"
          size="sm"
          onClick={() => handleEdit(deliverable)}
        />
        <IconButton
          icon={<Trash2 size={14} />}
          ariaLabel="Supprimer"
          intent="ghost"
          size="sm"
          onClick={() => handleDelete(deliverable.id)}
        />
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Alerts */}
      <ProductAlerts alerts={alerts} />

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text">Livrables du produit</h3>
        <Button label="+ Ajouter un livrable" intent="primary" size="sm" onClick={handleCreate} />
      </div>

      {deliverables.length === 0 ? (
        <div className="rounded-md border border-border bg-surface-muted p-4">
          <p className="text-sm text-muted">Aucun livrable défini pour ce produit.</p>
          <p className="text-xs text-muted mt-2">
            Utilisez « Livrés au client » pour ce que le client reçoit ; « Obligations fournisseur » pour ce que le fournisseur doit nous livrer (ex. accès plateforme, formation).
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Section Livrables client */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-text">Livrables client</h4>
            <p className="text-xs text-muted">Ce qui est remis au client.</p>
            {customerDeliverables.length === 0 ? (
              <p className="text-sm text-muted italic">Aucun.</p>
            ) : (
              <div className="space-y-2">{customerDeliverables.map(renderDeliverableRow)}</div>
            )}
          </div>

          {/* Section Obligations fournisseur */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-text">Obligations fournisseur</h4>
            <p className="text-xs text-muted">Ce que le fournisseur doit nous livrer (validation commande, suivi partenaire, SLA).</p>
            {supplierDeliverables.length === 0 ? (
              <p className="text-sm text-muted italic">Aucune.</p>
            ) : (
              <div className="space-y-2">{supplierDeliverables.map(renderDeliverableRow)}</div>
            )}
          </div>
        </div>
      )}

      <Modal
        title={editingDeliverable ? "Modifier le livrable" : "Créer un livrable"}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        showDefaultButtons={false}
        showCloseButton={true}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Input
              label="Nom du livrable"
              placeholder="Nom du livrable"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: "" });
              }}
              state={errors.name ? "error" : "default"}
              required
            />
            {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Description</label>
            <textarea
              className="w-full h-20 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description du livrable"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Type de livrable</label>
            <Select
              options={DELIVERABLE_TYPE_OPTIONS.map((opt) => opt.label)}
              value={DELIVERABLE_TYPE_OPTIONS.find((o) => o.value === form.deliverable_type)?.label ?? "Livré au client"}
              onChange={(value) => {
                const option = DELIVERABLE_TYPE_OPTIONS.find((o) => o.label === value);
                if (option) setForm({ ...form, deliverable_type: option.value });
              }}
            />
            <p className="text-xs text-muted mt-0.5">
              Livré au client : ce que le client reçoit. Attendu du fournisseur : ce que le fournisseur doit fournir.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Type de preuve</label>
              <Select
                options={EVIDENCE_TYPE_OPTIONS.map((opt) => opt.label)}
                value={EVIDENCE_TYPE_OPTIONS.find((opt) => opt.value === form.evidence_type)?.label || ""}
                onChange={(value) => {
                  const option = EVIDENCE_TYPE_OPTIONS.find((opt) => opt.label === value);
                  if (option) setForm({ ...form, evidence_type: option.value });
                }}
              />
            </div>
            <div>
              <Input
                label="Ordre d'affichage"
                type="number"
                min="0"
                value={form.sort_order.toString()}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="rounded-md border border-border bg-surface-muted/50 p-3 space-y-2">
            <h4 className="text-sm font-medium text-text">Comportement</h4>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_mandatory"
                checked={form.is_mandatory}
                onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })}
                className="h-4 w-4 rounded border-border"
              />
              <label htmlFor="is_mandatory" className="text-sm text-text">
                Obligatoire
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_customer_visible"
                checked={form.is_customer_visible}
                onChange={(e) => setForm({ ...form, is_customer_visible: e.target.checked })}
                className="h-4 w-4 rounded border-border"
              />
              <label htmlFor="is_customer_visible" className="text-sm text-text">
                Visible pour le client
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="acceptance_required"
                checked={form.acceptance_required}
                onChange={(e) => setForm({ ...form, acceptance_required: e.target.checked })}
                className="h-4 w-4 rounded border-border"
              />
              <label htmlFor="acceptance_required" className="text-sm text-text">
                Acceptation requise
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              onClick={() => setIsModalOpen(false)}
            />
            <Button label="Enregistrer" intent="primary" size="sm" onClick={handleSave} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
